import { useMemo } from "react";
import type { UserRole } from "../../../../lib/rbac/types.ts";
import { useUser } from "../../../../lib/rbac/useUser.ts";
import type { OffboardingRequest } from "../schemas/index.ts";
import type { OffboardingSectionType } from "../schemas/offboarding-questionnaire.schema.ts";
import type { ClearanceDepartment } from "../components/clearance-summary/clearance-summary.tsx";

export const ROLE_TO_DEPARTMENT: Partial<Record<UserRole, ClearanceDepartment>> = {
    "IT Manager": "IT",
    Payroll: "PAYROLL",
    ADMIN: "ADMIN",
    "Reporting Manager": "RM",
    HR: "HR",
};

export const DEPARTMENT_TO_SECTION: Record<ClearanceDepartment, OffboardingSectionType> = {
    IT: "IT_CLEARANCE",
    PAYROLL: "PAYROLL_CLEARANCE",
    ADMIN: "ADMIN_CLEARANCE",
    RM: "RM_CLEARANCE",
    HR: "HR_CLEARANCE",
};

export const CLEARANCE_DEPARTMENT_PRIORITY: ClearanceDepartment[] = [
    "IT",
    "PAYROLL",
    "ADMIN",
    "RM",
];

const DEPARTMENT_COMPLETED: Record<
    ClearanceDepartment,
    (request: OffboardingRequest) => boolean
> = {
    IT: (r) => r.itCompleted,
    PAYROLL: (r) => r.payrollCompleted,
    ADMIN: (r) => r.adminCompleted,
    RM: (r) => r.rmCompleted,
    HR: (r) => r.hrCompleted,
};

export function isDepartmentCompleted(
    department: ClearanceDepartment,
    request: OffboardingRequest,
): boolean {
    return DEPARTMENT_COMPLETED[department](request);
}

export function areAllOtherDepartmentsComplete(request: OffboardingRequest): boolean {
    return (
        request.itCompleted &&
        request.payrollCompleted &&
        request.adminCompleted &&
        request.rmCompleted
    );
}

export function areAllClearancesComplete(request: OffboardingRequest): boolean {
    return (
        request.itCompleted &&
        request.payrollCompleted &&
        request.adminCompleted &&
        request.rmCompleted &&
        request.hrCompleted
    );
}

export function shouldPromptExitInterview(request: OffboardingRequest): boolean {
    return areAllOtherDepartmentsComplete(request) && !request.exitInterviewCompleted;
}

export function getPrimaryClearanceDepartment(
    departments: ClearanceDepartment[],
): ClearanceDepartment | null {
    for (const dept of CLEARANCE_DEPARTMENT_PRIORITY) {
        if (departments.includes(dept)) {
            return dept;
        }
    }
    return null;
}

export function getSectionForDepartment(
    department: ClearanceDepartment,
): OffboardingSectionType {
    return DEPARTMENT_TO_SECTION[department];
}

export function resolveDepartmentsFromRoles(
    roles: UserRole[] | undefined,
): ClearanceDepartment[] {
    if (roles == null || roles.length === 0) return [];

    const seen = new Set<ClearanceDepartment>();
    const result: ClearanceDepartment[] = [];

    for (const role of roles) {
        const dept = ROLE_TO_DEPARTMENT[role];
        if (dept != null && !seen.has(dept)) {
            seen.add(dept);
            result.push(dept);
        }
    }

    return result;
}

export function useOffboardingRole() {
    const user = useUser();

    return useMemo(() => {
        const departments = resolveDepartmentsFromRoles(user?.roles);
        const isHR = departments.includes("HR");
        const clearanceDepartments = departments.filter((d) => d !== "HR");
        const isClearanceDeptApprover =
            !isHR && clearanceDepartments.length > 0;
        const primaryClearanceDepartment = getPrimaryClearanceDepartment(clearanceDepartments);
        const primarySectionType =
            primaryClearanceDepartment != null
                ? getSectionForDepartment(primaryClearanceDepartment)
                : null;

        return {
            user,
            isHR,
            isClearanceDeptApprover,
            departments,
            clearanceDepartments,
            primaryClearanceDepartment,
            primarySectionType,
            getSectionForDepartment,
            isDepartmentCompleted,
            areAllOtherDepartmentsComplete,
        };
    }, [user]);
}
